import React, { useState } from 'react';
import * as Popover from '@radix-ui/react-popover';
import { Info, X } from 'lucide-react';
import './InfoHint.css';

/**
 * Baustein — InfoHint.
 * Dezenter (i)-Knopf, der ein Erklär-Popover öffnet (Titel-Zeile + freier Inhalt).
 * Für kurze Konzept-Erklärungen neben Stats, Feldern oder Sektions-Titeln.
 * Schließt bei Klick außerhalb (Standard-Popover-Verhalten).
 *
 * Verwendung:
 *   <InfoHint title="Ziehchancen">
 *     <p>Wahrscheinlichkeit, in den ersten 7 Karten …</p>
 *   </InfoHint>
 *
 * `persistent` (intern): unterdrückt das Schließen bei Outside-Click. NUR für
 * `SyntaxHelpPopover` gedacht (dort soll man beim Tippen mitlesen können) —
 * neue Konsumenten nutzen IMMER das Default-Verhalten.
 *
 * `triggerLabel` (intern): überschreibt das aria-label/title des Triggers.
 * NUR für Konsumenten gedacht, die ein bestehendes aria-label beibehalten
 * müssen (z.B. `SyntaxHelpPopover` → „Syntax-Hilfe"); neue Konsumenten
 * verlassen sich auf das aus `title` abgeleitete Default-Label.
 */
export default function InfoHint({ title, children, side = 'bottom', align = 'start', persistent = false, triggerLabel }) {
  const [open, setOpen] = useState(false);

  return (
    <Popover.Root open={open} onOpenChange={setOpen} modal={false}>
      <Popover.Trigger asChild>
        <button
          type="button"
          className="info-hint__trigger"
          aria-label={triggerLabel || (title ? `Erklärung: ${title}` : 'Erklärung')}
          title={triggerLabel || title || 'Erklärung'}
        >
          <Info size={16} />
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          className="info-hint__content"
          side={side}
          align={align}
          sideOffset={6}
          onInteractOutside={persistent ? (e) => e.preventDefault() : undefined}
          onPointerDownOutside={persistent ? (e) => e.preventDefault() : undefined}
        >
          <div className="info-hint__header">
            <span className="info-hint__title">{title}</span>
            <Popover.Close asChild>
              <button type="button" className="info-hint__close" aria-label="Schließen">
                <X size={14} />
              </button>
            </Popover.Close>
          </div>
          <div className="info-hint__body">{children}</div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
